import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, IconButton, MenuItem, Stack, Switch, FormControlLabel, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { Section, Field, Metric } from "./ui";
import {
  useUpdateCompanyProfileMutation, useUpdateCompanyBrandingMutation,
  useUpdateCompanySecurityMutation, useCreateNumberForMutation, useDeleteNumberForMutation,
  useCreateIntegrationMutation, useUpdateIntegrationMutation, useDeleteIntegrationMutation,
  useCreateApiClientForMutation, useRevokeApiClientForMutation, useRegenerateApiClientForMutation,
  useDeleteApiClientForMutation, useCreateUserForMutation, useUpdateUserForMutation,
  useDeleteUserForMutation, useAssignSubscriptionForMutation, useGetPlansQuery, useGetRolesQuery,
} from "../../../Api/platformApi";

type TabProps = { companyId: number; data: any; isSuper: boolean };

const BUSINESS_TYPES = ["Events", "Restaurant", "Real Estate", "Retail", "Other"];
const STATUSES = ["trial", "active", "suspended"];
const CURRENCIES = ["CAD", "USD", "EUR", "GBP"];

// ──────────────────────────────────────────────────────────────────────────
// TAB 1 — Company Profile
// ──────────────────────────────────────────────────────────────────────────
export const ProfileTab: React.FC<TabProps> = ({ companyId, data }) => {
  const c = data.company;
  const [save, { isLoading }] = useUpdateCompanyProfileMutation();
  const [form, setForm] = useState<any>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm({
      name: c.name ?? "", legal_name: c.legal_name ?? "", business_type: c.business_type ?? "",
      website: c.website ?? "", description: c.description ?? "",
      primary_email: c.primary_email ?? "", billing_email: c.billing_email ?? "",
      primary_phone: c.primary_phone ?? "", support_phone: c.support_phone ?? "",
      address_street: c.address_street ?? "", address_city: c.address_city ?? "",
      address_province: c.address_province ?? "", country: c.country ?? "CA",
      address_postal_code: c.address_postal_code ?? "",
      timezone: c.timezone ?? "America/Toronto", currency: c.currency ?? "CAD",
      status: c.status ?? "trial",
    });
  }, [c]);

  const f = (k: string) => (v: string) => { setForm({ ...form, [k]: v }); setSaved(false); };
  const submit = async () => { await save({ id: companyId, body: form }).unwrap(); setSaved(true); };

  return (
    <Box>
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Profile saved.</Alert>}
      <Section title="Identity">
        <Grid container spacing={2}>
          <Field label="Company Name" value={form.name} onChange={f("name")} />
          <Field label="Slug" value={c.slug} onChange={() => {}} />
          <Field label="Company Type" value={form.business_type} onChange={f("business_type")} select>
            {BUSINESS_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Field>
          <Field label="Status" value={form.status} onChange={f("status")} select>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Field>
        </Grid>
      </Section>

      <Section title="Business Information">
        <Grid container spacing={2}>
          <Field label="Legal Name" value={form.legal_name} onChange={f("legal_name")} />
          <Field label="Website" value={form.website} onChange={f("website")} />
          <Field label="Description" value={form.description} onChange={f("description")} full multiline />
        </Grid>
      </Section>

      <Section title="Contact">
        <Grid container spacing={2}>
          <Field label="Primary Email" value={form.primary_email} onChange={f("primary_email")} />
          <Field label="Billing Email" value={form.billing_email} onChange={f("billing_email")} />
          <Field label="Phone" value={form.primary_phone} onChange={f("primary_phone")} />
          <Field label="Support Phone" value={form.support_phone} onChange={f("support_phone")} />
        </Grid>
      </Section>

      <Section title="Address">
        <Grid container spacing={2}>
          <Field label="Street" value={form.address_street} onChange={f("address_street")} full />
          <Field label="City" value={form.address_city} onChange={f("address_city")} />
          <Field label="Province / State" value={form.address_province} onChange={f("address_province")} />
          <Field label="Country" value={form.country} onChange={f("country")} />
          <Field label="Postal Code" value={form.address_postal_code} onChange={f("address_postal_code")} />
        </Grid>
      </Section>

      <Section title="Locale">
        <Grid container spacing={2}>
          <Field label="Timezone" value={form.timezone} onChange={f("timezone")} />
          <Field label="Currency" value={form.currency} onChange={f("currency")} select>
            {CURRENCIES.map((cu) => <MenuItem key={cu} value={cu}>{cu}</MenuItem>)}
          </Field>
        </Grid>
      </Section>

      <Button variant="contained" onClick={submit} disabled={isLoading || !form.name}>Save Profile</Button>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// TAB 2 — Branding
// ──────────────────────────────────────────────────────────────────────────
const COLORS = [
  ["primary_color", "Primary Color", "#1976d2"],
  ["secondary_color", "Secondary Color", "#9c27b0"],
  ["sidebar_color", "Sidebar Color", "#111827"],
  ["button_color", "Button Color", "#1976d2"],
  ["text_color", "Text Color", "#111827"],
] as const;

export const BrandingTab: React.FC<TabProps> = ({ companyId, data }) => {
  const c = data.company;
  const branding = (c.settings_json?.branding ?? {}) as any;
  const [save, { isLoading }] = useUpdateCompanyBrandingMutation();
  const [form, setForm] = useState<any>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm({
      logo: c.logo ?? branding.logo ?? "", small_logo: branding.small_logo ?? "",
      favicon: branding.favicon ?? "", login_background: branding.login_background ?? "",
      primary_color: branding.primary_color ?? "#1976d2", secondary_color: branding.secondary_color ?? "#9c27b0",
      sidebar_color: branding.sidebar_color ?? "#111827", button_color: branding.button_color ?? "#1976d2",
      text_color: branding.text_color ?? "#111827",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c]);

  const f = (k: string) => (v: string) => { setForm({ ...form, [k]: v }); setSaved(false); };
  const submit = async () => { await save({ id: companyId, body: form }).unwrap(); setSaved(true); };

  const previewAsCompany = () => {
    localStorage.setItem("intalk_company_id", String(companyId));
    window.open("/dashboard", "_blank");
  };

  return (
    <Box>
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Branding updated — sidebar, login, emails and reports will reflect these changes.</Alert>}
      <Section title="Logos & Images" subtitle="Paste an uploaded asset path or full URL.">
        <Grid container spacing={2}>
          <Field label="Company Logo" value={form.logo} onChange={f("logo")} />
          <Field label="Small Logo" value={form.small_logo} onChange={f("small_logo")} />
          <Field label="Favicon" value={form.favicon} onChange={f("favicon")} />
          <Field label="Login Background Image" value={form.login_background} onChange={f("login_background")} />
        </Grid>
      </Section>

      <Section title="Theme" subtitle="Drives the white-label engine across the tenant.">
        <Grid container spacing={2}>
          {COLORS.map(([key, label]) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Stack direction="row" spacing={1} alignItems="center">
                <input type="color" value={form[key] ?? "#000000"} onChange={(e) => f(key)(e.target.value)}
                  style={{ width: 44, height: 40, border: "none", background: "none", cursor: "pointer" }} />
                <TextField label={label} value={form[key] ?? ""} onChange={(e) => f(key)(e.target.value)} size="small" fullWidth />
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Section>

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={submit} disabled={isLoading}>Save Branding</Button>
        <Button variant="outlined" onClick={previewAsCompany}>Preview as Company</Button>
      </Stack>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// TAB 3 — Phone Numbers
// ──────────────────────────────────────────────────────────────────────────
const SMS_PROVIDERS = ["telnyx", "twilio", "plivo"];
const VOICE_PROVIDERS = ["3cx", "telnyx", "twilio"];

export const NumbersTab: React.FC<TabProps> = ({ companyId, data }) => {
  const numbers = data.numbers ?? [];
  const [createNumber] = useCreateNumberForMutation();
  const [deleteNumber] = useDeleteNumberForMutation();
  const [open, setOpen] = useState(false);
  const empty = { number: "", country_code: "+1", sms_enabled: true, voice_enabled: false, sms_provider: "telnyx", voice_provider: "3cx", status: "active" };
  const [form, setForm] = useState<any>(empty);

  const set = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.checked });
  const save = async () => { await createNumber({ companyId, body: form }).unwrap(); setOpen(false); setForm(empty); };
  const remove = async (id: number) => { if (window.confirm("Remove this number?")) await deleteNumber({ companyId, id }); };

  return (
    <Section title="Assigned Numbers" action={<Button variant="contained" size="small" onClick={() => { setForm(empty); setOpen(true); }}>Assign Number</Button>}>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>Number</TableCell><TableCell>Type</TableCell><TableCell>Provider</TableCell>
          <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {numbers.length === 0 && <TableRow><TableCell colSpan={5}>No numbers assigned.</TableCell></TableRow>}
          {numbers.map((n: any) => (
            <TableRow key={n.id}>
              <TableCell>{n.number}</TableCell>
              <TableCell>{[n.sms_enabled ? "SMS" : null, n.voice_enabled ? "Voice" : null].filter(Boolean).join(" + ") || "—"}</TableCell>
              <TableCell>{n.sms_provider ?? n.voice_provider ?? "—"}</TableCell>
              <TableCell><Chip size="small" label={n.status} color={n.status === "active" ? "success" : "default"} /></TableCell>
              <TableCell align="right"><IconButton size="small" color="error" onClick={() => remove(n.id)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Assign Number</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Number (E.164, e.g. +19057985515)" value={form.number} onChange={set("number")} fullWidth required />
            <TextField label="Country Code" value={form.country_code} onChange={set("country_code")} sx={{ width: 140 }} />
            <FormControlLabel control={<Switch checked={form.sms_enabled} onChange={toggle("sms_enabled")} />} label="SMS enabled" />
            {form.sms_enabled && (
              <TextField select label="SMS Provider" value={form.sms_provider} onChange={set("sms_provider")} fullWidth>
                {SMS_PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            )}
            <FormControlLabel control={<Switch checked={form.voice_enabled} onChange={toggle("voice_enabled")} />} label="Voice enabled" />
            {form.voice_enabled && (
              <TextField select label="Voice Provider" value={form.voice_provider} onChange={set("voice_provider")} fullWidth>
                {VOICE_PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!form.number}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// TAB 4 — Integrations
// ──────────────────────────────────────────────────────────────────────────
const INTEGRATION_TYPES = ["crm", "website", "pos", "other"];

export const IntegrationsTab: React.FC<TabProps> = ({ companyId, data }) => {
  const integrations = data.integrations ?? [];
  const [create] = useCreateIntegrationMutation();
  const [update] = useUpdateIntegrationMutation();
  const [remove] = useDeleteIntegrationMutation();
  const [open, setOpen] = useState(false);
  const empty = { name: "", type: "crm", url: "", webhook_url: "", status: "active" };
  const [form, setForm] = useState<any>(empty);
  const [editId, setEditId] = useState<number | null>(null);

  const set = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });
  const openCreate = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (i: any) => { setEditId(i.id); setForm({ name: i.name, type: i.type, url: i.url ?? "", webhook_url: i.webhook_url ?? "", status: i.status }); setOpen(true); };
  const save = async () => {
    if (editId) await update({ companyId, id: editId, body: form }).unwrap();
    else await create({ companyId, body: form }).unwrap();
    setOpen(false);
  };
  const del = async (id: number) => { if (window.confirm("Remove this integration?")) await remove({ companyId, id }); };

  return (
    <Section title="Connected Systems" subtitle="INTALK forwards inbound message events to each integration's reply webhook."
      action={<Button variant="contained" size="small" onClick={openCreate}>Add Integration</Button>}>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>Name</TableCell><TableCell>Type</TableCell><TableCell>URL</TableCell>
          <TableCell>Webhook URL</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {integrations.length === 0 && <TableRow><TableCell colSpan={6}>No integrations connected.</TableCell></TableRow>}
          {integrations.map((i: any) => (
            <TableRow key={i.id}>
              <TableCell>{i.name}</TableCell>
              <TableCell><Chip size="small" label={i.type} /></TableCell>
              <TableCell>{i.url}</TableCell>
              <TableCell><code style={{ fontSize: 12 }}>{i.webhook_url}</code></TableCell>
              <TableCell><Chip size="small" label={i.status} color={i.status === "active" ? "success" : "default"} /></TableCell>
              <TableCell align="right">
                <Button size="small" onClick={() => openEdit(i)}>Edit</Button>
                <IconButton size="small" color="error" onClick={() => del(i.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Edit Integration" : "Add Integration"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Name (e.g. DCC CRM)" value={form.name} onChange={set("name")} fullWidth required />
            <TextField select label="Type" value={form.type} onChange={set("type")} fullWidth>
              {INTEGRATION_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="URL (e.g. https://go2dcc.ca)" value={form.url} onChange={set("url")} fullWidth />
            <TextField label="Reply Webhook URL (e.g. https://go2dcc.ca/api/intalk/webhook)" value={form.webhook_url} onChange={set("webhook_url")} fullWidth />
            <TextField select label="Status" value={form.status} onChange={set("status")} fullWidth>
              <MenuItem value="active">active</MenuItem><MenuItem value="inactive">inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!form.name}>Save</Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// TAB 5 — API Access
// ──────────────────────────────────────────────────────────────────────────
export const ApiAccessTab: React.FC<TabProps> = ({ companyId, data }) => {
  const clients = data.api_clients ?? [];
  const [create] = useCreateApiClientForMutation();
  const [revoke] = useRevokeApiClientForMutation();
  const [regenerate] = useRegenerateApiClientForMutation();
  const [remove] = useDeleteApiClientForMutation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);

  const save = async () => { const res: any = await create({ companyId, body: { name } }).unwrap(); setNewKey(res.api_key); setName(""); };
  const regen = async (id: number) => {
    if (!window.confirm("Regenerate key? The old key stops working immediately.")) return;
    const res: any = await regenerate({ companyId, id }).unwrap(); setName(""); setNewKey(res.api_key); setOpen(true);
  };

  return (
    <Section title="API Keys" subtitle="Used when external apps (e.g. DCC CRM) call INTALK."
      action={<Button variant="contained" size="small" onClick={() => { setName(""); setNewKey(null); setOpen(true); }}>Generate Key</Button>}>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>Application Name</TableCell><TableCell>Key</TableCell><TableCell>Created</TableCell>
          <TableCell>Last Used</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {clients.length === 0 && <TableRow><TableCell colSpan={6}>No API keys yet.</TableCell></TableRow>}
          {clients.map((c: any) => (
            <TableRow key={c.id}>
              <TableCell>{c.name}</TableCell>
              <TableCell><code>{c.api_key_prefix}…</code></TableCell>
              <TableCell>{(c.created_at ?? "").slice(0, 10)}</TableCell>
              <TableCell>{c.last_used_at ?? "never"}</TableCell>
              <TableCell><Chip size="small" label={c.status} color={c.status === "active" ? "success" : "error"} /></TableCell>
              <TableCell align="right">
                <IconButton size="small" title="Regenerate" onClick={() => regen(c.id)}><VpnKeyIcon fontSize="small" /></IconButton>
                {c.status === "active" && <IconButton size="small" title="Revoke" onClick={() => revoke({ companyId, id: c.id })}><BlockIcon fontSize="small" /></IconButton>}
                <IconButton size="small" color="error" title="Delete" onClick={() => remove({ companyId, id: c.id })}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Generate API Key</DialogTitle>
        <DialogContent>
          {!newKey ? (
            <TextField label="Application Name (e.g. DCC CRM)" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ mt: 1 }} required />
          ) : (
            <Alert severity="success" sx={{ mt: 1 }}>
              <Typography variant="body2" gutterBottom>Copy this key now — it will not be shown again:</Typography>
              <code style={{ wordBreak: "break-all" }}>{newKey}</code>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{newKey ? "Done" : "Cancel"}</Button>
          {!newKey && <Button variant="contained" onClick={save} disabled={!name}>Generate</Button>}
        </DialogActions>
      </Dialog>
    </Section>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// TAB 6 — Subscription
// ──────────────────────────────────────────────────────────────────────────
export const SubscriptionTab: React.FC<TabProps> = ({ companyId, data, isSuper }) => {
  const sub = data.subscription;
  const invoices = data.invoices ?? [];
  const { data: plansData } = useGetPlansQuery();
  const plans = plansData?.data ?? [];
  const [assign, { isLoading }] = useAssignSubscriptionForMutation();
  const [planId, setPlanId] = useState<string>("");
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => { setPlanId(sub?.subscription_plan_id ? String(sub.subscription_plan_id) : ""); }, [sub]);

  const change = async () => { if (planId) await assign({ companyId, body: { subscription_plan_id: Number(planId), billing_cycle: cycle } }).unwrap(); };

  return (
    <Box>
      <Section title="Current Plan">
        {sub ? (
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="h6" fontWeight={700}>{sub.plan?.name ?? "—"}</Typography>
              <Typography variant="body2" color="text.secondary">
                ${(sub.billing_cycle === "yearly" ? sub.plan?.yearly_price : sub.plan?.monthly_price) ?? "0"}/{sub.billing_cycle === "yearly" ? "year" : "month"}
              </Typography>
            </Box>
            <Chip label={sub.status} color={sub.status === "active" ? "success" : sub.status === "past_due" ? "error" : "default"} />
            <Box>
              <Typography variant="caption" color="text.secondary">Next invoice</Typography>
              <Typography variant="body2">{(sub.next_invoice_date ?? "—").toString().slice(0, 10)}</Typography>
            </Box>
            {sub.plan && (
              <Box>
                <Typography variant="caption" color="text.secondary">Included</Typography>
                <Typography variant="body2">
                  {sub.plan.included_sms ?? "—"} SMS · {sub.plan.included_users ?? "—"} users · {sub.plan.included_numbers ?? "—"} numbers
                </Typography>
              </Box>
            )}
          </Stack>
        ) : <Typography color="text.secondary">No active subscription.</Typography>}
      </Section>

      <Section title="Change Plan">
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField select size="small" label="Plan" value={planId} onChange={(e) => setPlanId(e.target.value)} sx={{ minWidth: 220 }}>
            {plans.map((p: any) => <MenuItem key={p.id} value={String(p.id)}>{p.name} — ${p.monthly_price}/mo</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Billing Cycle" value={cycle} onChange={(e) => setCycle(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="monthly">Monthly</MenuItem><MenuItem value="yearly">Yearly</MenuItem>
          </TextField>
          <Button variant="contained" onClick={change} disabled={isLoading || !planId}>Apply Plan</Button>
        </Stack>
      </Section>

      <Section title="Recent Invoices">
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Invoice</TableCell><TableCell>Date</TableCell><TableCell>Total</TableCell><TableCell>Status</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {invoices.length === 0 && <TableRow><TableCell colSpan={4}>No invoices yet.</TableCell></TableRow>}
            {invoices.map((inv: any) => (
              <TableRow key={inv.id}>
                <TableCell>{inv.invoice_number ?? `#${inv.id}`}</TableCell>
                <TableCell>{(inv.created_at ?? "").slice(0, 10)}</TableCell>
                <TableCell>${inv.total ?? "0"}</TableCell>
                <TableCell><Chip size="small" label={inv.status} color={inv.status === "paid" ? "success" : inv.status === "failed" ? "error" : "default"} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {isSuper && <Typography variant="caption" color="text.secondary">Use the Billing module for manual charges and full invoice history.</Typography>}
      </Section>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// TAB 7 — Usage
// ──────────────────────────────────────────────────────────────────────────
export const UsageTab: React.FC<TabProps> = ({ data, isSuper }) => {
  const u = data.usage ?? {};
  const daily = data.usage_daily ?? [];
  const maxCount = Math.max(1, ...daily.map((d: any) => d.count));

  return (
    <Box>
      <Section title="This Month">
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Metric label="SMS Sent" value={u.sms_sent ?? 0} />
          <Metric label="SMS Received" value={u.sms_received ?? 0} />
          <Metric label="Segments" value={u.segments ?? 0} />
          {isSuper && <Metric label="Provider Cost" value={`$${u.provider_cost ?? 0}`} color="error.main" />}
        </Stack>
      </Section>

      <Section title="Daily SMS Usage">
        {daily.length === 0 ? <Typography color="text.secondary">No usage this month.</Typography> : (
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 160, overflowX: "auto", pt: 2 }}>
            {daily.map((d: any) => (
              <Box key={d.date} sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 28 }}>
                <Typography variant="caption">{d.count}</Typography>
                <Box sx={{ width: 18, height: `${(d.count / maxCount) * 110}px`, bgcolor: "primary.main", borderRadius: 1 }} />
                <Typography variant="caption" sx={{ fontSize: 9, mt: 0.5 }}>{d.date.slice(5)}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Section>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// TAB 8 — Users
// ──────────────────────────────────────────────────────────────────────────
export const UsersTab: React.FC<TabProps> = ({ companyId, data }) => {
  const members = data.members ?? [];
  const { data: rolesData } = useGetRolesQuery();
  const roles = rolesData?.data ?? [];
  const [create] = useCreateUserForMutation();
  const [update] = useUpdateUserForMutation();
  const [remove] = useDeleteUserForMutation();
  const [open, setOpen] = useState(false);
  const empty = { name: "", username: "", email: "", password: "", role_id: "", status: "active" };
  const [form, setForm] = useState<any>(empty);

  const set = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });
  const save = async () => { await create({ companyId, body: { ...form, role_id: form.role_id || null } }).unwrap(); setOpen(false); setForm(empty); };
  const changeRole = async (id: number, role_id: any) => { await update({ companyId, id, body: { role_id: role_id || null } }); };
  const toggleStatus = async (m: any) => { await update({ companyId, id: m.id, body: { status: m.status === "active" ? "disabled" : "active" } }); };
  const del = async (id: number) => { if (window.confirm("Remove this user?")) await remove({ companyId, id }); };

  return (
    <Section title="Company Users" action={<Button variant="contained" size="small" onClick={() => { setForm(empty); setOpen(true); }}>Invite User</Button>}>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Role</TableCell>
          <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {members.length === 0 && <TableRow><TableCell colSpan={5}>No users yet.</TableCell></TableRow>}
          {members.map((m: any) => (
            <TableRow key={m.id}>
              <TableCell>{m.user?.name ?? m.user?.username}</TableCell>
              <TableCell>{m.user?.email}</TableCell>
              <TableCell>
                <TextField select size="small" value={m.role_id ?? ""} sx={{ minWidth: 140 }} onChange={(e) => changeRole(m.id, e.target.value)}>
                  <MenuItem value="">— none —</MenuItem>
                  {roles.map((r: any) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                </TextField>
              </TableCell>
              <TableCell><Chip size="small" label={m.status} color={m.status === "active" ? "success" : "default"} /></TableCell>
              <TableCell align="right">
                <Button size="small" onClick={() => toggleStatus(m)}>{m.status === "active" ? "Disable" : "Enable"}</Button>
                <IconButton size="small" color="error" onClick={() => del(m.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Name" value={form.name} onChange={set("name")} fullWidth required />
            <TextField label="Username" value={form.username} onChange={set("username")} fullWidth required />
            <TextField label="Email" value={form.email} onChange={set("email")} fullWidth />
            <TextField label="Password" type="password" value={form.password} onChange={set("password")} fullWidth required />
            <TextField select label="Role" value={form.role_id} onChange={set("role_id")} fullWidth>
              <MenuItem value="">— none —</MenuItem>
              {roles.map((r: any) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!form.name || !form.username || !form.password}>Invite</Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// TAB 9 — Security
// ──────────────────────────────────────────────────────────────────────────
export const SecurityTab: React.FC<TabProps> = ({ companyId, data }) => {
  const security = (data.company.settings_json?.security ?? {}) as any;
  const audit = data.audit_logs ?? [];
  const [save, { isLoading }] = useUpdateCompanySecurityMutation();
  const [form, setForm] = useState<any>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm({
      allowed_ips: security.allowed_ips ?? "", webhook_secret: security.webhook_secret ?? "",
      api_rate_limit: security.api_rate_limit ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.company]);

  const f = (k: string) => (v: string) => { setForm({ ...form, [k]: v }); setSaved(false); };
  const submit = async () => {
    await save({ id: companyId, body: { ...form, api_rate_limit: form.api_rate_limit === "" ? null : Number(form.api_rate_limit) } }).unwrap();
    setSaved(true);
  };

  return (
    <Box>
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Security settings saved.</Alert>}
      <Section title="Settings">
        <Grid container spacing={2}>
          <Field label="Allowed IPs (comma separated)" value={form.allowed_ips} onChange={f("allowed_ips")} full />
          <Field label="Webhook Secret" value={form.webhook_secret} onChange={f("webhook_secret")} />
          <Field label="API Rate Limit (per minute)" value={form.api_rate_limit} onChange={f("api_rate_limit")} type="number" />
        </Grid>
        <Button variant="contained" sx={{ mt: 2 }} onClick={submit} disabled={isLoading}>Save Security</Button>
      </Section>

      <Section title="Audit Log">
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>When</TableCell><TableCell>Action</TableCell><TableCell>Description</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {audit.length === 0 && <TableRow><TableCell colSpan={3}>No activity recorded.</TableCell></TableRow>}
            {audit.map((a: any) => (
              <TableRow key={a.id}>
                <TableCell>{(a.created_at ?? "").toString().slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell><Chip size="small" label={a.action} /></TableCell>
                <TableCell>{a.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <Divider sx={{ my: 1 }} />
    </Box>
  );
};
